import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createInsuranceCancelRequest,
  getInsuranceOfferInfo,
  getInsurancePolicyPdf,
  getInsuranceReceiptPdf,
  openInsurancePdf,
} from '../../../api/endpoints/insurance';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { ApiError } from '../../../api/types/common';
import type { TamamliyoOfferData } from '../../../api/types/insurance';
import { PhoneField } from '../../../components/PhoneField';

const cancelSchema = z.object({
  tcKimlikNo: z.string().regex(/^\d{11}$/, '11 haneli TC Kimlik No'),
  musteriAdi: z.string().min(1, 'Ad Soyad gerekli'),
  musteriEmail: z.string().email('Geçerli e-posta girin'),
  partnerEmail: z.string().email('Geçerli e-posta girin'),
  musteriTel: z.string().min(10, 'Geçerli telefon girin'),
});
type CancelValues = z.infer<typeof cancelSchema>;

function DetailRow({ label, value }: { label: string; value?: string | number }) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600, width: 220 }}>{label}</TableCell>
      <TableCell>{value ?? '—'}</TableCell>
    </TableRow>
  );
}

/**
 * "Poliçelerim" — "poliçe listele" ucu YOK, bu yüzden `teklifId` ile tekil sorgu
 * (`Insurance/GetOfferInfo`) + PDF indirme + "İptal Talebi" (`Insurance/CancelRequest`).
 */
export default function InsurancePoliciesPage() {
  const [teklifId, setTeklifId] = useState('');
  const [detail, setDetail] = useState<TamamliyoOfferData | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelResult, setCancelResult] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState<'policy' | 'receipt' | null>(null);

  const lookup = useMutation({
    mutationFn: () => getInsuranceOfferInfo(teklifId.trim()),
    onSuccess: (res) => setDetail(res.data ?? null),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CancelValues>({ resolver: zodResolver(cancelSchema) });

  const cancel = useMutation({
    mutationFn: (values: CancelValues) =>
      createInsuranceCancelRequest({ teklifId: Number(teklifId.trim()), ...values }),
    onSuccess: (res) => {
      setCancelResult(res.message || res.durumu || 'İptal talebi alındı.');
      setCancelOpen(false);
      reset();
    },
  });

  const downloadPdf = async (kind: 'policy' | 'receipt') => {
    setPdfBusy(kind);
    setPdfError(null);
    try {
      const id = teklifId.trim();
      const res = kind === 'policy' ? await getInsurancePolicyPdf(id) : await getInsuranceReceiptPdf(id);
      if (!res.rawPdfContent) throw new Error('Belge içeriği boş döndü.');
      openInsurancePdf(res.rawPdfContent, `${kind === 'policy' ? 'police' : 'makbuz'}-${id}.pdf`);
    } catch (err) {
      setPdfError(err instanceof ApiError || err instanceof Error ? err.message : 'Belge alınamadı.');
    } finally {
      setPdfBusy(null);
    }
  };

  const teklif = detail?.teklifBilgileri;
  const kisi = detail?.kisiselBilgiler;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Poliçelerim
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Sağlayıcıda toplu "poliçe listeleme" ucu bulunmuyor — poliçe detayına teklif numarası
        ile ulaşılır.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
          <TextField
            size="small"
            label="Teklif No"
            value={teklifId}
            onChange={(e) => setTeklifId(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={() => lookup.mutate()}
            disabled={!teklifId.trim() || lookup.isPending}
          >
            {lookup.isPending ? 'Sorgulanıyor…' : 'Sorgula'}
          </Button>
        </Stack>
        <ApiErrorAlert error={lookup.error} sx={{ mt: 2 }} />
      </Paper>

      {cancelResult && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCancelResult(null)}>
          {cancelResult}
        </Alert>
      )}

      {detail && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Poliçe / Teklif Detayı
          </Typography>
          {detail.errorMessage && <Alert severity="warning">{detail.errorMessage}</Alert>}
          <TableContainer>
            <Table size="small">
              <TableBody>
                <DetailRow label="Teklif No" value={teklif?.teklifId} />
                <DetailRow label="Poliçe No" value={teklif?.policeNo} />
                <DetailRow label="Fiyat" value={teklif?.fiyat} />
                <DetailRow label="Oluşturulma" value={teklif?.olusturulmaTarihi} />
                <DetailRow
                  label="Sigortalı"
                  value={[kisi?.ad, kisi?.soyad].filter(Boolean).join(' ') || undefined}
                />
                <DetailRow label="TC Kimlik No" value={kisi?.tcKimlikNo} />
                <DetailRow label="E-posta" value={kisi?.email} />
                <DetailRow label="Ürün" value={detail.urunBilgileri?.urunAdi} />
                <DetailRow label="Sigorta Şirketi" value={detail.sigortaSirketiBilgileri?.tamAdi} />
              </TableBody>
            </Table>
          </TableContainer>

          {pdfError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {pdfError}
            </Alert>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" disabled={pdfBusy !== null} onClick={() => downloadPdf('policy')}>
              {pdfBusy === 'policy' ? 'Alınıyor…' : 'Poliçe PDF'}
            </Button>
            <Button variant="outlined" disabled={pdfBusy !== null} onClick={() => downloadPdf('receipt')}>
              {pdfBusy === 'receipt' ? 'Alınıyor…' : 'Makbuz PDF'}
            </Button>
            <Button color="error" variant="outlined" onClick={() => setCancelOpen(true)}>
              İptal Talebi
            </Button>
          </Stack>
        </Paper>
      )}

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>İptal Talebi — Teklif {teklifId}</DialogTitle>
        <form onSubmit={handleSubmit((values) => cancel.mutate(values))}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="TC Kimlik No"
                  error={!!errors.tcKimlikNo}
                  helperText={errors.tcKimlikNo?.message}
                  {...register('tcKimlikNo')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Müşteri Ad Soyad"
                  error={!!errors.musteriAdi}
                  helperText={errors.musteriAdi?.message}
                  {...register('musteriAdi')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Müşteri E-posta"
                  error={!!errors.musteriEmail}
                  helperText={errors.musteriEmail?.message}
                  {...register('musteriEmail')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Partner E-posta"
                  error={!!errors.partnerEmail}
                  helperText={errors.partnerEmail?.message}
                  {...register('partnerEmail')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  control={control}
                  name="musteriTel"
                  defaultValue=""
                  render={({ field }) => (
                    <PhoneField
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      label="Müşteri Telefon"
                      error={!!errors.musteriTel}
                      helperText={errors.musteriTel?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <ApiErrorAlert error={cancel.error} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelOpen(false)}>Vazgeç</Button>
            <Button type="submit" variant="contained" color="error" disabled={cancel.isPending}>
              {cancel.isPending ? 'Gönderiliyor…' : 'İptal Talebi Gönder'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
